import { useState, useMemo } from "react";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Compass, Sparkles, Mail, Check } from "lucide-react";
import { QUIZ_QUESTIONS, PARCOURS, computeParcours } from "@/lib/quiz-data";
import { api } from "@/lib/api";

export default function Quiz() {
  const [step, setStep] = useState(0); // 0 = intro, 1..N = questions, N+1 = result, N+2 = enrolled
  const [answers, setAnswers] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const totalQuestions = QUIZ_QUESTIONS.length;
  const isIntro = step === 0;
  const isQuestion = step >= 1 && step <= totalQuestions;
  const isResult = step === totalQuestions + 1;
  const isEnrolled = step === totalQuestions + 2;
  const currentQ = isQuestion ? QUIZ_QUESTIONS[step - 1] : null;

  const parcoursId = useMemo(() => (isResult || isEnrolled ? computeParcours(answers) : null), [answers, isResult, isEnrolled]);
  const parcours = parcoursId ? PARCOURS[parcoursId] : null;

  const start = () => { setAnswers([]); setStep(1); };

  const choose = (parcoursIdForAnswer) => {
    const next = [...answers, parcoursIdForAnswer];
    setAnswers(next);
    setStep(step + 1);
  };

  const back = () => {
    if (isQuestion && step > 1) {
      setAnswers(answers.slice(0, -1));
      setStep(step - 1);
    }
  };

  const restart = () => { setAnswers([]); setStep(0); };

  const enroll = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/parcours/enroll", {
        email,
        name,
        parcours_id: parcoursId,
      });
      toast.success("C'est parti ! Le premier email arrive bientôt.");
      setStep(totalQuestions + 2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  const progress = isQuestion ? ((step - 1) / totalQuestions) * 100 : isResult || isEnrolled ? 100 : 0;

  return (
    <div
      data-testid="quiz-section"
      className="relative rounded-3xl overflow-hidden bg-white border border-[#DDE3E8] jt-medium-shadow"
    >
      {/* Progress bar */}
      <div className="h-1 bg-[#E4EBEF]">
        <div className="h-full bg-[#4A6D82] transition-all duration-500" style={{ width: `${progress}%` }} />
      </div>

      <div className="p-6 md:p-12">
        {/* INTRO */}
        {isIntro && (
          <div className="jt-fadeUp text-center max-w-2xl mx-auto py-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#E4EBEF] text-[#4A6D82] text-xs uppercase tracking-widest mb-6">
              <Compass className="w-3.5 h-3.5" /> Quiz d'orientation
            </div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] text-[#1E2933] mb-5">
              Où en es-tu dans <em className="not-italic text-[#4A6D82]">ta marche</em> avec Jésus ?
            </h2>
            <p className="text-[#6B7A85] text-lg mb-8 leading-relaxed">
              6 questions courtes. Nous te proposerons un parcours de croissance sur-mesure pour t'accompagner par email dans ta saison actuelle.
            </p>
            <button
              onClick={start}
              data-testid="quiz-start"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#4A6D82] text-white text-base hover:bg-[#2E4959] transition jt-blue-glow group"
            >
              Commencer le quiz
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
            <p className="text-xs text-[#6B7A85] mt-4">⏱ Moins de 2 minutes · 100% confidentiel</p>
          </div>
        )}

        {/* QUESTION */}
        {isQuestion && currentQ && (
          <div key={step} className="jt-fadeUp">
            <div className="flex items-center justify-between mb-8">
              <span className="text-xs uppercase tracking-widest text-[#4A6D82]">Question {step}/{totalQuestions}</span>
              {step > 1 && (
                <button
                  onClick={back}
                  data-testid="quiz-back"
                  className="inline-flex items-center gap-1.5 text-sm text-[#6B7A85] hover:text-[#4A6D82]"
                >
                  <ArrowLeft className="w-4 h-4" /> Retour
                </button>
              )}
            </div>
            <h3 className="font-serif text-3xl md:text-4xl text-[#1E2933] leading-tight mb-8" data-testid="quiz-question">
              {currentQ.q}
            </h3>
            <div className="grid gap-3">
              {currentQ.answers.map((a, i) => (
                <button
                  key={i}
                  data-testid={`quiz-answer-${i}`}
                  onClick={() => choose(a.parcours)}
                  className="group flex items-center justify-between text-left px-5 py-4 rounded-2xl border border-[#DDE3E8] bg-white hover:border-[#4A6D82] hover:bg-[#E4EBEF]/50 transition-all"
                >
                  <span className="text-[#1E2933] text-base md:text-lg pr-4">{a.label}</span>
                  <span className="h-9 w-9 rounded-full border border-[#DDE3E8] group-hover:border-[#4A6D82] group-hover:bg-[#4A6D82] group-hover:text-white flex items-center justify-center shrink-0 transition-all">
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* RESULT */}
        {isResult && parcours && (
          <div className="jt-fadeUp" data-testid="quiz-result">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#E4EBEF] text-[#4A6D82] text-xs uppercase tracking-widest mb-4">
                <Sparkles className="w-3.5 h-3.5" /> Ton parcours recommandé
              </div>
              <p className="text-sm text-[#6B7A85] uppercase tracking-widest mb-2">Parcours {parcours.number}</p>
              <h3 className="font-serif text-4xl md:text-6xl text-[#1E2933] leading-tight">
                {parcours.title}
              </h3>
              <p className="text-lg text-[#4A6D82] mt-2 italic font-serif">{parcours.subtitle}</p>
              <p className="text-[#1E2933] mt-6 max-w-2xl mx-auto text-lg">
                {parcours.description}
              </p>
            </div>

            <div className={`relative rounded-2xl overflow-hidden p-6 md:p-8 bg-gradient-to-br ${parcours.color} text-white mb-8`}>
              <p className="text-xs uppercase tracking-widest text-white/70 mb-2">Ce que tu vas recevoir</p>
              <p className="font-serif text-2xl md:text-3xl leading-tight max-w-xl">
                {parcours.emails} emails espacés, pratiques et bibliques, pour t'accompagner semaine après semaine.
              </p>
            </div>

            <form onSubmit={enroll} className="grid gap-3 max-w-xl mx-auto" data-testid="quiz-enroll-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <input
                  data-testid="quiz-name"
                  type="text"
                  placeholder="Ton prénom"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="px-4 py-3 rounded-full bg-white border border-[#DDE3E8] text-sm focus:ring-2 focus:ring-[#4A6D82] focus:border-[#4A6D82]"
                />
                <input
                  data-testid="quiz-email"
                  type="email"
                  required
                  placeholder="Ton email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="px-4 py-3 rounded-full bg-white border border-[#DDE3E8] text-sm focus:ring-2 focus:ring-[#4A6D82] focus:border-[#4A6D82]"
                />
              </div>
              <div className="flex flex-wrap gap-2 justify-between items-center mt-2">
                <button
                  type="button"
                  onClick={restart}
                  className="text-sm text-[#6B7A85] hover:text-[#4A6D82] underline underline-offset-4"
                >
                  ↺ Refaire le quiz
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="quiz-submit"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#4A6D82] text-white text-sm hover:bg-[#2E4959] transition disabled:opacity-60 jt-blue-glow"
                >
                  <Mail className="w-4 h-4" />
                  {loading ? "..." : "Commencer mon parcours"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ENROLLED CONFIRMATION */}
        {isEnrolled && parcours && (
          <div className="jt-fadeUp text-center max-w-xl mx-auto py-6" data-testid="quiz-enrolled">
            <div className="h-16 w-16 rounded-full bg-[#4A6D82] text-white flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8" strokeWidth={2.5} />
            </div>
            <h3 className="font-serif text-4xl md:text-5xl text-[#1E2933] mb-4">
              C'est parti !
            </h3>
            <p className="text-lg text-[#1E2933]/80 mb-2">
              Ton premier email du parcours <strong className="text-[#4A6D82]">{parcours.title}</strong> arrive dans quelques minutes.
            </p>
            <p className="text-sm text-[#6B7A85] mb-8">
              Pense à vérifier ta boîte principale (et ton dossier promotions/spam au cas où).
            </p>
            <button
              onClick={restart}
              className="text-sm text-[#4A6D82] hover:text-[#2E4959] underline underline-offset-4"
            >
              ↺ Refaire le quiz pour un proche
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
